// Copyright The OpenTelemetry Authors
// SPDX-License-Identifier: Apache-2.0

import { NextPage } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Footer from '../../components/Footer';
import Layout from '../../components/Layout';
import * as S from '../../styles/Policy.styled';

interface IPolicySection {
  heading: string;
  body: string;
}

interface IPolicy {
  title: string;
  updated: string;
  sections: IPolicySection[];
}

const policies: Record<string, IPolicy> = {
  shipping: {
    title: 'Shipping policy',
    updated: 'Last updated July 2026',
    sections: [
      {
        heading: 'Delivery times',
        body: 'Orders placed before 3pm ship the same day; everything else ships within 24 hours. Standard delivery arrives in 3 to 5 business days. Telescopes over 20kg travel freight and arrive in 5 to 7 business days.',
      },
      {
        heading: 'Shipping costs',
        body: 'Shipping is free on all orders over $50. Below that, a flat rate is calculated at checkout based on your address. No surprises on the confirmation page.',
      },
      {
        heading: 'Order tracking',
        body: 'Every order is traced end to end. You receive a tracking ID the moment your order is confirmed, and you can follow every hop from our warehouse to your doorstep.',
      },
      {
        heading: 'Damaged in transit',
        body: 'Optics are fragile and we pack like it. If anything still arrives damaged, contact us within 48 hours with a photo and we ship a replacement immediately, no return required first.',
      },
    ],
  },
  returns: {
    title: 'Returns & refunds',
    updated: 'Last updated July 2026',
    sections: [
      {
        heading: '30-day returns',
        body: 'You have 30 days from delivery to return any item in its original packaging. Cloudy season, changed your mind, or the aperture bug bit and you want something bigger: all valid reasons, no questions asked.',
      },
      {
        heading: 'How to start a return',
        body: 'Reply to your order confirmation email with your order number. We send a prepaid label the same day. Drop the parcel at any carrier point and you are done.',
      },
      {
        heading: 'Refund timing',
        body: 'Refunds go back to your original payment method within 5 business days of the item reaching our warehouse. We will email you at every step.',
      },
      {
        heading: 'Exchanges',
        body: 'Want a different scope instead of a refund? Mention it when you start the return and we reserve the replacement right away so it does not sell out while your return travels.',
      },
    ],
  },
  privacy: {
    title: 'Privacy policy',
    updated: 'Last updated July 2026',
    sections: [
      {
        heading: 'What we collect',
        body: 'A session identifier, the items you browse and buy, and the address you give us at checkout. That is it. We use this to run the shop and to show you relevant recommendations.',
      },
      {
        heading: 'What we observe',
        body: 'This storefront is heavily instrumented with OpenTelemetry, so every page view, click, and API call produces traces, metrics, and logs. That telemetry exists to demonstrate observability, not to profile you.',
      },
      {
        heading: 'What we never do',
        body: 'We do not sell your data, share it with advertisers, or send you email you did not ask for. Newsletter signups only ever receive what they subscribed to.',
      },
      {
        heading: 'Your choices',
        body: 'You can empty your cart and abandon your session at any time, and it expires on its own. Nothing here persists beyond the demo environment.',
      },
    ],
  },
  terms: {
    title: 'Terms of service',
    updated: 'Last updated July 2026',
    sections: [
      {
        heading: 'The short version',
        body: 'The OpenTelemetry Demo store is a demonstration storefront operated by Grafana Field Engineering on top of the OpenTelemetry demo. Browsing and buying here is play-acting: no goods exist, no money moves, and no telescopes are shipped.',
      },
      {
        heading: 'Orders and payment',
        body: 'The checkout accepts a prefilled test card only. Placing an order exercises the same services a real shop would use, which is the entire point, but the confirmation email, tracking ID, and delivery are simulated.',
      },
      {
        heading: 'Acceptable use',
        body: 'Feel free to click everything, break things, and watch the telemetry. That is what this store is for. Just do not rely on it for actual astronomy purchases; your local telescope shop will treat you better.',
      },
      {
        heading: 'Liability',
        body: 'Since nothing is sold, our liability is limited to the mild disappointment of learning the Starsense Explorer in your cart is not real.',
      },
    ],
  },
};

const PolicyPage: NextPage = () => {
  const { query, isReady } = useRouter();
  const slug = query.slug?.toString() ?? '';
  const policy = policies[slug];

  return (
    <Layout>
      <S.Policy>
        {!isReady ? null : policy ? (
          <>
            <S.Eyebrow>OpenTelemetry Demo</S.Eyebrow>
            <S.Title>{policy.title}</S.Title>
            <S.Updated>{policy.updated}</S.Updated>
            {policy.sections.map(({ heading, body }) => (
              <div key={heading}>
                <S.SectionTitle>{heading}</S.SectionTitle>
                <S.Text>{body}</S.Text>
              </div>
            ))}
            <S.DemoNote>
              This is a demonstration storefront. This policy is part of the demo and does not create any
              real obligations, mostly because nothing here is real, including the telescopes.
            </S.DemoNote>
          </>
        ) : (
          <S.NotFound>
            <S.Title>Policy not found</S.Title>
            <S.Text>
              That page does not exist. Head back to the <Link href="/">store</Link>.
            </S.Text>
          </S.NotFound>
        )}
      </S.Policy>
      <Footer />
    </Layout>
  );
};

export default PolicyPage;
